import type { Section } from '@pen/shared';
import type { ActProgress } from './embed';

type CheckpointSection = Extract<Section, { kind: 'checkpoint' }>;

/** A checkpoint belongs to the preceding film, until another player replaces it. A lesson-wide `top` film owns none. */
export function filmsBefore(sections: Section[]): Record<string, string | undefined> {
  const films: Record<string, string | undefined> = {};
  let priorFilm: string | undefined;
  for (const section of sections) {
    if (section.kind === 'embed') priorFilm = section.embed.acts.length ? section.id : undefined;
    else if (section.kind === 'video' || section.kind === 'film' && !section.top) priorFilm = section.id;
    else if (section.kind === 'checkpoint') films[section.id] = priorFilm;
  }
  return films;
}

export function reachedFilmCheckpoints(sections: Section[], films: Record<string, string | undefined>, filmId: string, progress: ActProgress): CheckpointSection[] {
  return sections.filter((section): section is CheckpointSection => section.kind === 'checkpoint' && films[section.id] === filmId && (progress.ended || (section.afterAct !== null && progress.act > section.afterAct)));
}

/** Chapter indices have the same zero-based semantics as embed acts. */
export function chapterAtTime(chapters: { start: number }[], time: number): number {
  let chapter = -1;
  for (let i = 0; i < chapters.length; i++) {
    if (chapters[i]!.start > time) break;
    chapter = i;
  }
  return chapter;
}

/** What `window.film.chapters` of a lesson film declares: `heading` is the lesson heading the chapter explains. */
export interface FilmChapter { t: number; title: string; heading: string }
export interface FilmLink { index: number; t: number; title: string; sectionId: string }

export function readFilmChapters(value: unknown): FilmChapter[] {
  if (!Array.isArray(value)) return [];
  const chapters: FilmChapter[] = [];
  for (const item of value as unknown[]) {
    if (!item || typeof item !== 'object') return [];
    const { t, title, heading } = item as Record<string, unknown>;
    if (typeof t !== 'number' || !Number.isFinite(t) || t < 0 || typeof title !== 'string' || typeof heading !== 'string') return [];
    chapters.push({ t, title, heading });
  }
  return chapters;
}

const normalized = (text: string) => text.trim().replace(/\s+/g, ' ').toLocaleLowerCase();

/** The first chapter explaining a heading owns that heading's "watch this part" control; headings the lesson lacks are skipped. */
export function chapterLinks(chapters: FilmChapter[], sections: Section[]): FilmLink[] {
  const byHeading = new Map<string, string>();
  for (const section of sections) {
    if (section.kind !== 'text' || section.title === null) continue;
    const key = normalized(section.title);
    if (!byHeading.has(key)) byHeading.set(key, section.id);
  }
  const claimed = new Set<string>();
  const links: FilmLink[] = [];
  chapters.forEach((chapter, index) => {
    const sectionId = byHeading.get(normalized(chapter.heading));
    if (sectionId === undefined || claimed.has(sectionId)) return;
    claimed.add(sectionId);
    links.push({ index, t: chapter.t, title: chapter.title, sectionId });
  });
  return links;
}

export type FilmMessage =
  | { type: 'fullscreen' }
  | { type: 'state'; time: number; duration: number; playing: boolean }
  | { type: 'chapter'; index: number }
  | { type: 'stop'; checkpointId: string };

export interface FilmMessageContext {
  origin: string;
  /** The iframe's window: messages from anything else are ignored. */
  source: unknown;
  filmId: string;
  checkpointIds: ReadonlySet<string>;
  chapterCount: number;
}

/** Validates a `pen-film-*` message; returns null for anything not sent by this lesson's own film about this lesson. */
export function parseFilmMessage(event: { origin: string; source: unknown; data: unknown }, context: FilmMessageContext): FilmMessage | null {
  if (event.origin !== context.origin || !context.source || event.source !== context.source) return null;
  const data = event.data as Record<string, unknown> | null;
  if (!data || typeof data !== 'object' || data.id !== context.filmId) return null;
  switch (data.type) {
    case 'pen-film-fullscreen': return { type: 'fullscreen' };
    case 'pen-film-state': {
      const { time, duration, playing } = data;
      if (typeof time !== 'number' || !Number.isFinite(time) || typeof duration !== 'number' || !Number.isFinite(duration) || duration <= 0 || typeof playing !== 'boolean') return null;
      return { type: 'state', time, duration, playing };
    }
    case 'pen-film-chapter': {
      const { index } = data;
      return typeof index === 'number' && Number.isInteger(index) && index >= 0 && index < context.chapterCount ? { type: 'chapter', index } : null;
    }
    case 'pen-film-stop': {
      const { checkpointId } = data;
      return typeof checkpointId === 'string' && context.checkpointIds.has(checkpointId) ? { type: 'stop', checkpointId } : null;
    }
    default: return null;
  }
}
